// Helper για Google Analytics 4 με Google Consent Mode v2.
// The GA script is loaded ONLY after the user accepts the statistics.

import DataEventEnum from '@/core/lib/services/enum/DataEventEnum.js'
import GTagEventEnum from '@/core/lib/services/enum/GTagEventEnum.js'
import DataEventService from '@/core/lib/services/event/DataEventService.js'
import {clone} from '@/core/lib/utils/helper/index.js'

let loaded = false

window.dataLayer = window.dataLayer || []

export function gtag() {
  window.dataLayer.push(arguments)
}

// Default: everything denied until consent is given (applies to ALL IDs)
gtag('consent', 'default', {
  analytics_storage : 'denied',
  ad_storage        : 'denied',
  ad_user_data      : 'denied',
  ad_personalization: 'denied',
  wait_for_update   : 500
})

/**
 * Updates Consent Mode — it is global and covers all IDs.
 * @param {{ analytics: boolean, marketing: boolean }} granted
 */
export function updateConsent({analytics, marketing}) {
  gtag('consent', 'update', {
    analytics_storage : analytics ? 'granted' : 'denied',
    ad_storage        : marketing ? 'granted' : 'denied',
    ad_user_data      : marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied'
  })
}

/**
 * Loads gtag.js ONCE and configures every ID.
 * @param {Function} loadScript - the CookieConsent.loadScript
 * @param {Array} GA_IDS - Array with the GA IDS.
 * @param GA_CONFIG -
 */
export async function loadGA(loadScript, GA_IDS, GA_CONFIG) {
  if (loaded || !GA_IDS?.length) return

  const defaultConfig = {
    send_page_view: true,
    anonymize_ip  : true
  }

  loaded = true

  // The script is loaded only once, using the first ID
  await loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_IDS[0]}`, {async: ''})

  gtag('js', new Date())
  GA_IDS?.forEach((id) => gtag('config', id, GA_CONFIG || defaultConfig))

  // First page view for the page that is already open
  // trackPageView(window.location.pathname + window.location.search, document.title)

  DataEventService.On(DataEventEnum.EVENT, handleGADataEvent)
}

export function isGALoaded() {
  return loaded
}

/** Disables all IDs for the current page (e.g. when consent is revoked). */
export function disableGA(GA_IDS) {
  if (!GA_IDS?.length) return
  GA_IDS?.forEach((id) => {
    window[`ga-disable-${id}`] = true
  })
}

// Without send_to, an event goes to ALL configured IDs.
// With send_to, it goes only to the given ones: trackEvent('x', {}, 'G-BBBBBBBBBB')
export function trackPageView(path, title, sendTo) {
  if (!loaded) return
  gtag('event', 'page_view', {
    page_location: window.location.origin + path,
    page_title   : title,
    ...(sendTo && {send_to: sendTo})
  })
}

export function trackEvent(name, params = {}, sendTo) {
  if (!loaded) return
  gtag('event', name, {
    ...params,
    ...(sendTo && {send_to: sendTo})
  })
}

const handleGADataEvent = ($data) => {
  if (!loaded) return
  const data = clone($data)

  switch (data.Event) {
  case DataEventEnum.LOGIN:
    trackEvent(GTagEventEnum.LOGIN, {method: data.Payload.Method})
    break

  case DataEventEnum.SIGN_UP:
    trackEvent(GTagEventEnum.SIGN_UP, {method: data.Payload.Method})
    break

  case DataEventEnum.ADD_TO_CART:
    delete data.Payload.cartTotalQuantity
    trackEvent(GTagEventEnum.ADD_TO_CART, data.Payload)
    break

  case DataEventEnum.REMOVE_FROM_CART:
    delete data.Payload.cartTotalQuantity
    trackEvent(GTagEventEnum.REMOVE_FROM_CART, data.Payload)
    break

  case DataEventEnum.VIEW_ITEM:
    trackEvent(GTagEventEnum.VIEW_ITEM, data.Payload)
    break

  case DataEventEnum.BEGIN_CHECKOUT:
    delete data.Payload.cartTotalQuantity
    trackEvent(GTagEventEnum.BEGIN_CHECKOUT, data.Payload)
    break

  case DataEventEnum.ADD_SHIPPING_INFO:
    trackEvent(GTagEventEnum.ADD_SHIPPING_INFO, data.Payload)
    break

  case DataEventEnum.ADD_PAYMENT_INFO:
    trackEvent(GTagEventEnum.ADD_PAYMENT_INFO, data.Payload)
    break

  case DataEventEnum.PURCHASE:
    delete data.Payload.user
    trackEvent(GTagEventEnum.PURCHASE, data.Payload)
    break

  case DataEventEnum.SEARCH:
    trackEvent(GTagEventEnum.SEARCH, {search_term: data.Payload})
    break
  }
}
